import { useContext, useEffect, useState } from 'react'

import { useSelector } from 'react-redux'
import { isString } from 'lodash'
import { Button, Dropdown, Menu } from 'antd'
import { faHospital } from '@fortawesome/pro-regular-svg-icons'
import { faBriefcase, faBusinessTime, faCircleXmark, faFilter, faGraduationCap, faSchool, faSort } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GlobalFiltersContext } from '../../../Providers/GlobalFiltersProvider'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import IconLabel, { FontSizes } from '../../../Components/icons/IconLabel'
import StatesSelector from '../../../Components/shared/Selectors/StatesSelector'
import InstitutionsSelector from '../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import SchoolsSelector from '../../../Components/shared/Selectors/School/SchoolsSelector'
import InternshipStatesOrderBy from '../../../Components/shared/InternshipStatesOrderBy'
import { OrderBy, OrderDirections } from '../../../utils/constants'
import { isInstitutionMaccsUser, isSchoolMaccsUser } from '../../../utils/roles'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import SpecialitiesSelector from '../../../Components/shared/Selectors/SpecialitiesSelector'
import WorkingRegimesSelector from '../../../Components/shared/Selectors/WorkingRegimesSelector'
import { isNumber } from '../../../utils'
import { getMaccsLinkedInstitutions } from '../../../utils/api/school'
import SpecialityYearsSelector from '../../../Components/shared/Selectors/SpecialityYearsSelector'

export const getActiveFiltersCount = (filters) => {
  const filtersState = filters ?? {}
  let count = 0

  if (isString(filtersState.institutions)) {
    count += 1
  }

  if (isString(filtersState.schools)) {
    count += 1
  }

  if (isNumber(filtersState.workingRegime)) {
    count += 1
  }

  if (Array.isArray(filtersState.specialities) && filtersState.specialities.length > 0) {
    count += 1
  }

  if (Array.isArray(filtersState.states) && filtersState.states.length > 0) {
    count += 1
  }

  if (Array.isArray(filtersState.studyYears) && filtersState.studyYears.length > 0) {
    count += 1
  }

  return count
}

const MaccsInternshipFilters = ({ useOrder = true, onFilterChange, onResetFilters, context }) => {
  const { user } = useContext(GlobalContext)
  const { internshipFilters, setInternshipFilters } = useContext(GlobalFiltersContext)
  const { institutions, order, schools, specialities, states, studyYears, workingRegime } = internshipFilters

  const [localInstitutions, setLocalInstitutions] = useState([])

  useEffect(() => {
    if (isSchoolMaccsUser(user)) {
      getMaccsLinkedInstitutions(user, user.school).then(({ data }) => setLocalInstitutions(data))
    }
  }, [user.id])

  const t = useSelector(selectTranslate)

  const [hovered, setHovered] = useState(false)

  const handleFilterChange = (value, key) => {
    onFilterChange()
    setInternshipFilters({ ...internshipFilters, [key]: value })
  }

  const activeFiltersCount = getActiveFiltersCount(internshipFilters)

  return (
    <Dropdown
      overlay={
        <Menu>
          <Menu.ItemGroup title={t('Filters')}>
            <Menu inlineIndent={0}>
              <div className='h-spacing' />
              <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faBusinessTime} text={t('common.internship')} />}>
                <Menu.Item key='state' disabled style={{ cursor: 'default' }}>
                  <StatesSelector
                    context={context}
                    padding={0}
                    onCheck={s => handleFilterChange(s, 'states')}
                    values={states}
                  />
                </Menu.Item>
              </Menu.ItemGroup>

              {!isInstitutionMaccsUser(user) && (
                <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faHospital} text={t('common.institutions')} />}>
                  <Menu.Item disabled style={{ cursor: 'default' }}>
                    <InstitutionsSelector
                      values={institutions}
                      institutions={localInstitutions}
                      onChange={i => handleFilterChange(i, 'institutions')}
                      style={{ width: '210px' }}
                    />
                  </Menu.Item>
                </Menu.ItemGroup>
              )}
              {!isSchoolMaccsUser(user) && (
                <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faSchool} text={t('common.schools')} />}>
                  <Menu.Item disabled style={{ cursor: 'default' }}>
                    <SchoolsSelector
                      values={schools}
                      onChange={s => handleFilterChange(s, 'schools')}
                      style={{ width: '210px' }}
                    />
                  </Menu.Item>
                </Menu.ItemGroup>
              )}

              <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faGraduationCap} text={t('common.specialities')} />}>
                <Menu.Item disabled style={{ cursor: 'default', height: 'fit-content' }}>
                  <SpecialitiesSelector
                    multiple='multiple'
                    values={specialities}
                    onChange={s => handleFilterChange(s, 'specialities')}
                    style={{ minWidth: '210px', maxWidth: '420px' }}
                  />
                </Menu.Item>
              </Menu.ItemGroup>

              <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faGraduationCap} text={t('common.study_year')} />}>
                <Menu.Item disabled style={{ cursor: 'default', height: 'fit-content' }}>
                  <SpecialityYearsSelector
                    multiple='multiple'
                    values={studyYears}
                    onChange={s => handleFilterChange(s, 'studyYears')}
                    style={{ minWidth: '210px', maxWidth: '420px' }}
                  />
                </Menu.Item>
              </Menu.ItemGroup>

              <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faBriefcase} text={t('common.working_regime')} />}>
                <Menu.Item disabled style={{ cursor: 'default', height: 'fit-content' }}>
                  <WorkingRegimesSelector
                    multiple='multiple'
                    values={workingRegime}
                    onChange={w => handleFilterChange(w, 'workingRegime')}
                    style={{ width: '210px' }}
                  />
                </Menu.Item>
              </Menu.ItemGroup>

              {useOrder && (
                <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faSort} text={t('common.order_by')} />}>
                  <Menu.Item disabled style={{ cursor: 'default' }}>
                    <InternshipStatesOrderBy
                      value={order?.by ?? OrderBy.CREATED_AT}
                      onChange={ob => setInternshipFilters({ ...internshipFilters, order: { ...internshipFilters.order, by: ob } })}
                      direction={order?.direction ?? OrderDirections.DESC}
                      onDirectionChange={sd => setInternshipFilters({ ...internshipFilters, order: { ...internshipFilters.order, direction: sd } })}
                    />
                  </Menu.Item>
                </Menu.ItemGroup>
              )}
            </Menu>
          </Menu.ItemGroup>
        </Menu>
      }
      overlayClassName='custom-overlay'
      trigger={['click']}
    >
      <div
        className='filter-button-wrapper'
        style={{ position: 'relative', display: 'inline-block' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {activeFiltersCount > 0 && hovered && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              right: '7px',
              top: '5px',
              zIndex: '100',
              cursor: 'pointer'
            }}
          >
            <FontAwesomeIcon
              size='xl' onClick={(e) => {
                onResetFilters()
                e.stopPropagation()
              }} icon={faCircleXmark}
            />
          </span>
        )}

        <Button style={activeFiltersCount > 0 ? { color: '#52c41a', borderColor: '#52c41a' } : {}} className='flex-button'>
          <FontAwesomeIcon icon={faFilter} />
          {activeFiltersCount > 0 && <span style={{ marginLeft: '5px' }}> {activeFiltersCount} {activeFiltersCount === 1 ? t('filters.count.filter') : t('filters.count.filters')}</span>}
        </Button>
      </div>
    </Dropdown>
  )
}

export default MaccsInternshipFilters
